import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import { isNil } from 'lodash-es'
import {
  AuthorisedUser,
  CrudActions,
  EntityType,
  HaircutType,
  NegotiationActions,
  NegotiationDetail,
  NegotiationEvent,
  NegotiationFieldMeta,
  NegotiationUpdateAttributes,
  SpreadsheetMapping,
  SpreadsheetMappingType,
  SpreadsheetSettlementVenue,
  ValidationIssue
} from 'api'
import { ModalConsumerProps, withModal } from '../modal'
import { Groupbox, ModalContent, ModalFooter, ModalWindow } from 'view/components/panels'
import { ParseResultPanel, UploadDropzoneEmpty, UploadDropzoneFull } from '../upload'
import { IncomingMappingDialog } from './IncomingMappingDialog'
import { SpreadsheetMappingSelectboxField } from '../entity'
import { PendingIncomingPanel, PendingIncomingPanelProps } from './PendingIncomingPanel'
import { ButtonStrip, TextButton, TextButtonType } from 'view/components/buttons'
import { cloneMapping, getDefaultMapping, parseClipboard, SpreadsheetParseResult } from '../upload/model'
import { parseIncomingSpreadsheet as defaultParseIncomingSpreadsheet } from './parseIncomingSpreadsheet'
import { validateIncomingParseResult as defaultValidateIncomingParseResult } from './validateIncomingParseResult'
import { excelService, ioService } from 'services'
import { NotificationActions, NotificationType, ShowGrowlArgs } from 'store/notification'
import { canEditNegotiationDetailSettlementType, transformHaircutInput, transformRateInput } from '../datagrid'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { createSortedSpreadsheetMappingIdsSelector } from 'selectors/spreadsheetMappingSelectors'
import {
  incomingNegotiationDetailsByCollectionId,
  incomingNegotiationDetailsByNegotiationIdGetterSelector,
  incomingNegotiationDetailsByRfqIdGetterSelector
} from 'selectors/negotiations'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'
import { IncomingDialogColumnsProps } from './IncomingMappingDialogColumns'
import { Nullable } from 'utils'
import { severityError } from '../upload/utils'
import { isNotNil } from 'utils/predicate'

export type ParseIncomingSpreadsheet = (
  filename: string,
  rows: any[],
  spreadsheetMapping: SpreadsheetMapping
) => IncomingSpreadsheetParseResult<IncomingPriceUpload>

export interface IncomingPriceUpload {
  rate: Nullable<number>
  haircut: Nullable<number>
  haircutType: Nullable<HaircutType>
  settlementType: Nullable<SpreadsheetSettlementVenue>
  dataIssues: ValidationIssue[]
  rfqId: Nullable<string>
  negotiationId: Nullable<string>
}

export interface IncomingSpreadsheetParseRowResult<T> {
  priceUpload: T
  parseIssues: ValidationIssue[]
}

export interface IncomingSpreadsheetParseResult<T> extends SpreadsheetParseResult {
  priceUploads: T[]
}

export type ProcessRowData = (opts: { uploadFile?: File; clipboard?: string }) => Promise<any[]>

export type IncomingUploadDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps {
  initialFile?: File
  initialClipboard?: string
  validateIncomingParseResult?: typeof defaultValidateIncomingParseResult
  parseIncomingSpreadsheet?: ParseIncomingSpreadsheet
  processFileClipboardToRowData?: ProcessRowData
  spreadsheetMappingType?: SpreadsheetMappingType
  PendingIncomingPanelComponent?: React.ComponentType<PendingIncomingPanelProps>
  MappingDialogColumnComponent?: React.ComponentType<IncomingDialogColumnsProps>

  onClose?: () => unknown
}

interface StateProps {
  authorisedUser: AuthorisedUser
  spreadsheetMappings: string[]
  getSpreadsheetMapping: (mappingId: string) => SpreadsheetMapping | null
  getIncomingNegotiationDetailsByRfqId: (rfqId: string) => NegotiationDetail[]
  getIncomingNegotiationDetailsByNegotiationId: (negotiationId: string) => NegotiationDetail[]
  getIncomingNegotiationDetailsByCollectionId: (collectionId: string) => NegotiationDetail[]
}

interface DispatchProps {
  onUpdateNegotiationsBulk: typeof NegotiationActions.updateValuesBulk
  onDeleteSpreadsheetMapping: (mappingId: string) => unknown
  onShowGrowl: (growlArgs: ShowGrowlArgs) => unknown
}

interface IncomingUploadDialogState {
  uploadFile?: File
  clipboard?: string
  rowData: any[] | null
  parseResult: IncomingSpreadsheetParseResult<IncomingPriceUpload> | null
  priceUploads: IncomingPriceUpload[]
  spreadsheetMappingId: string | null
}

const mapStateToProps = createStructuredSelector({
  authorisedUser: createAuthorisedUserSelector(),
  spreadsheetMappings: createSortedSpreadsheetMappingIdsSelector(
    createSelector(
      (_: {}, ownProps: IncomingUploadDialogProps) => ownProps.spreadsheetMappingType ?? SpreadsheetMappingType.INCOMING,
      spreadsheetMappingType => spreadsheetMappingType
    )
  ),
  getSpreadsheetMapping: createEntityGetterSelector(EntityType.SPREADSHEET_MAPPING),
  getIncomingNegotiationDetailsByRfqId: incomingNegotiationDetailsByRfqIdGetterSelector,
  getIncomingNegotiationDetailsByNegotiationId: incomingNegotiationDetailsByNegotiationIdGetterSelector,
  getIncomingNegotiationDetailsByCollectionId: incomingNegotiationDetailsByCollectionId
})

const mapDispatchToProps = {
  onUpdateNegotiationsBulk: NegotiationActions.updateValuesBulk,
  onDeleteSpreadsheetMapping: CrudActions.spreadsheetMappingDelete,
  onShowGrowl: NotificationActions.showGrowl
}

export const Content = styled(ModalContent)`
  display: flex;
  gap: 20px;
  flex-direction: row;
  justify-content: space-between;
  padding: 0;
  margin: 0 24px;
  width: 1000px;
  min-height: 500px;
  border-bottom: 1px solid ${constants.CORE_PRIMARY_1};
`

const UploadContainer = styled.section`
  width: 450px;
`

const PendingContainer = styled.section`
  width: 530px;
`
const defaultProcessFileClipboardToRowData: ProcessRowData = async ({ uploadFile, clipboard }) => {
  if (uploadFile) {
    const newFileData = await ioService.readFile(uploadFile)
    return await excelService.parseXlsx(newFileData, { fileType: uploadFile.type })
  } else if (clipboard) {
    return parseClipboard(clipboard)
  }
  return []
}

export const IncomingUploadDialogComponent: React.FC<IncomingUploadDialogProps> = props => {
  const {
    initialFile,
    initialClipboard,
    onClose,
    authorisedUser,
    spreadsheetMappings,
    getSpreadsheetMapping,
    getIncomingNegotiationDetailsByRfqId,
    getIncomingNegotiationDetailsByNegotiationId,
    getIncomingNegotiationDetailsByCollectionId,
    validateIncomingParseResult = defaultValidateIncomingParseResult,
    parseIncomingSpreadsheet = defaultParseIncomingSpreadsheet,
    processFileClipboardToRowData = defaultProcessFileClipboardToRowData,
    spreadsheetMappingType = SpreadsheetMappingType.INCOMING,
    PendingIncomingPanelComponent = PendingIncomingPanel,
    MappingDialogColumnComponent,
    onUpdateNegotiationsBulk,
    onDeleteSpreadsheetMapping,
    onShowGrowl,
    showDialog,
    showModal
  } = props

  const negotiationDetailGetters = {
    getIncomingNegotiationDetailsByRfqId,
    getIncomingNegotiationDetailsByNegotiationId,
    getIncomingNegotiationDetailsByCollectionId
  }
  const [componentState, setState] = useState<IncomingUploadDialogState>({
    uploadFile: initialFile,
    clipboard: initialClipboard,
    rowData: null,
    parseResult: null,
    priceUploads: [],
    spreadsheetMappingId: null
  })

  const { uploadFile, clipboard, spreadsheetMappingId, rowData, parseResult, priceUploads } = componentState

  const loadRowData = async () => {
    try {
      setState(state => ({ ...state, rowData: null, parseResult: null }))
      const processedRowData = await processFileClipboardToRowData({ uploadFile, clipboard })
      setState(state => ({ ...state, rowData: processedRowData }))
    } catch (err) {
      onShowGrowl({
        type: NotificationType.Error,
        title: Translation.TC_UPLOAD_ERROR_TITLE,
        message: Translation.SC_UPLOAD_ERROR_MESSAGE,
        messageArgs: err
      })
    }
  }

  const parseRowData = () => {
    const spreadsheetMapping = getSelectedMapping()

    if ((uploadFile || clipboard) && rowData && spreadsheetMapping) {
      const newParseResult = parseIncomingSpreadsheet(uploadFile?.name ?? 'Clipboard', rowData, spreadsheetMapping)
      const validatedPriceUploads = validateIncomingParseResult(newParseResult, negotiationDetailGetters)

      setState(state => ({
        ...state,
        parseResult: newParseResult,
        priceUploads: validatedPriceUploads
      }))
    } else {
      setState(state => ({ ...state, parseResult: null }))
    }
  }

  const getSelectedMapping = () =>
    spreadsheetMappingId ? getSpreadsheetMapping(spreadsheetMappingId) : getDefaultMapping(spreadsheetMappingType)

  const ensureSelectedMapping = () => {
    const result = getSelectedMapping()

    if (isNil(result)) {
      throw new Error('Unable to load spreadsheet configuration')
    }

    return result
  }

  const canUpload =
    !!parseResult &&
    parseResult.issues.length === 0 &&
    priceUploads.length > 0 &&
    priceUploads.every(priceUpload => priceUpload.dataIssues.filter(severityError).length === 0)

  const handleUpdateMapping = (mappingId: string) => {
    setState({ ...componentState, spreadsheetMappingId: mappingId })
  }

  const handleDropFiles = (files: File[]) => {
    setState({ ...componentState, uploadFile: files[0], clipboard: undefined })
  }

  const handlePaste = (clipboardContent: string) => {
    setState({ ...componentState, uploadFile: undefined, clipboard: clipboardContent })
  }

  const handleNewMapping = () => {
    showMappingDialog(cloneMapping(ensureSelectedMapping(), authorisedUser.id), Locale.translate(Translation.TC_CREATE_MAPPING))
  }

  const handleEditMapping = () => {
    showMappingDialog(ensureSelectedMapping(), Locale.translate(Translation.TC_UPDATE_MAPPING))
  }

  const handleDeleteMapping = async () => {
    const mapping = ensureSelectedMapping()

    if (mapping.id === null) {
      throw new Error('No spreadsheet mapping is selected')
    }

    const result = await showDialog({
      type: NotificationType.Confirm,
      title: Translation.TC_PROMPT_TITLE_DELETE_SPREADSHEET_MAPPING,
      message: Translation.SC_PROMPT_MSG_DELETE_SPREADSHEET_MAPPING,
      messageArgs: { name: mapping.name },
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO
    })

    if (result === Translation.TC_BUTTON_YES) {
      onDeleteSpreadsheetMapping(mapping.id)
      setState({ ...componentState, spreadsheetMappingId: null })
    }
  }

  const showMappingDialog = async (mapping: SpreadsheetMapping, title: string) => {
    if (!parseResult) {
      throw new Error('Spreadsheet has not been loaded')
    }

    const mappingId = await showModal(
      <IncomingMappingDialog
        mapping={mapping}
        title={title}
        parseResult={parseResult}
        parseIncomingSpreadsheet={parseIncomingSpreadsheet}
        MappingDialogColumnsComponent={MappingDialogColumnComponent}
      />
    )

    if (mappingId) {
      setState({ ...componentState, spreadsheetMappingId: mappingId })
    }
  }

  const handleUpload = () => {
    if (canUpload) {
      const negotiationUpdateAttributesList = priceUploads.reduce<NegotiationUpdateAttributes[]>(
        (acc, { rfqId, negotiationId, rate, haircut, haircutType, settlementType }) => {
          if (isNil(rfqId) && isNil(negotiationId)) {
            throw new Error('Uploading prices - null RFQ and Negotiation ID')
          }
          const negotiationDetails = negotiationId
            ? getIncomingNegotiationDetailsByNegotiationId(negotiationId)
            : rfqId
            ? getIncomingNegotiationDetailsByRfqId(rfqId)
            : null

          if (isNil(negotiationDetails)) {
            return acc
          }
          const updateAttributesList: NegotiationUpdateAttributes[] = negotiationDetails
            .filter(negotiationDetail => negotiationDetail.availableEvents.includes(NegotiationEvent.UPDATE_ATTRIBUTES))
            .map(negotiationDetail => ({
              negotiationId: negotiationDetail.negotiation.negotiationId,
              updatedValues: {
                [NegotiationFieldMeta.RATE]: isNil(rate) ? undefined : transformRateInput(rate, negotiationDetail.referenceRate),
                [NegotiationFieldMeta.HAIRCUT]: isNil(haircut)
                  ? undefined
                  : transformHaircutInput(haircut, authorisedUser.userPreference.haircutSign, false, negotiationDetail.direction),
                [NegotiationFieldMeta.HAIRCUT_TYPE]: isNil(haircutType) ? undefined : haircutType,
                [NegotiationFieldMeta.SETTLEMENT_VENUE]:
                  canEditNegotiationDetailSettlementType(negotiationDetail) && isNotNil(settlementType) ? settlementType : undefined
              },
              updatedRawValues: {
                [NegotiationFieldMeta.RATE]: isNil(rate) ? undefined : rate,
                [NegotiationFieldMeta.HAIRCUT]: isNil(haircut) ? undefined : haircut,
                [NegotiationFieldMeta.HAIRCUT_TYPE]: isNil(haircutType) ? undefined : haircutType,
                [NegotiationFieldMeta.SETTLEMENT_VENUE]: isNil(settlementType) ? undefined : settlementType
              },
              meta: {
                haircutSign: authorisedUser.userPreference.haircutSign
              }
            }))

          return [...acc, ...updateAttributesList]
        },
        []
      )

      onUpdateNegotiationsBulk(negotiationUpdateAttributesList, false)

      if (onClose) {
        onClose()
      }
    } else {
      throw new Error('No prices are ready to be uploaded')
    }
  }

  useEffect(() => {
    if (spreadsheetMappings.length === 1) {
      setState(state => ({ ...state, spreadsheetMappingId: spreadsheetMappings[0] }))
    }
    loadRowData()
  }, [])

  useEffect(() => {
    loadRowData()
  }, [uploadFile, clipboard])

  useEffect(() => {
    parseRowData()
  }, [spreadsheetMappingId, rowData, getSpreadsheetMapping])

  return (
    <ModalWindow title={Locale.translate(Translation.TC_UPLOAD_PRICES)}>
      <Content>
        <>
          <UploadContainer>
            <Groupbox title={Locale.translate(Translation.TC_UPLOAD_FILE)}>
              {uploadFile || clipboard ? (
                <UploadDropzoneFull
                  uploadFile={uploadFile}
                  clipboard={clipboard}
                  handleDropFiles={handleDropFiles}
                  handlePaste={handlePaste}
                />
              ) : (
                <UploadDropzoneEmpty
                  narrative={Locale.translate(Translation.SC_INCOMING_DROPZONE_NARRATIVE)}
                  handleDropFiles={handleDropFiles}
                  handlePaste={handlePaste}
                />
              )}
            </Groupbox>
            {uploadFile || clipboard ? (
              <Groupbox title={Locale.translate(Translation.TC_COLUMN_MAPPINGS)}>
                <ParseResultPanel
                  parseResult={parseResult}
                  itemField="priceUploads"
                  successMessage={Translation.SC_INCOMING_SPREADSHEET_SUCCESS}
                  errorMessage={Translation.SC_INCOMING_SPREADSHEET_ERROR}
                  errorNarrative={Translation.SC_UPLOAD_ERROR_NARRATIVE}
                />
                <SpreadsheetMappingSelectboxField
                  label={Locale.translate(Translation.SC_SAVED_MAPPINGS)}
                  placeholder={Locale.translate(Translation.SC_SYSTEM_MAPPING)}
                  value={spreadsheetMappingId}
                  canClear={true}
                  onUpdate={handleUpdateMapping}
                  mappingType={spreadsheetMappingType}
                />
                <ButtonStrip horizontalLayout={constants.HorizontalLayout.Right} spacing="3px">
                  <TextButton type={TextButtonType.Bad} disabled={isNil(spreadsheetMappingId)} onClick={handleDeleteMapping}>
                    {Locale.translate(Translation.TC_BUTTON_DELETE)}
                  </TextButton>
                  <TextButton type={TextButtonType.Secondary} disabled={isNil(spreadsheetMappingId)} onClick={handleEditMapping}>
                    {Locale.translate(Translation.TC_BUTTON_UPDATE)}
                  </TextButton>
                  <TextButton type={TextButtonType.Secondary} onClick={handleNewMapping}>
                    {Locale.translate(Translation.TC_BUTTON_CREATE)}
                  </TextButton>
                </ButtonStrip>
              </Groupbox>
            ) : null}
          </UploadContainer>
          {parseResult && parseResult.issues.length === 0 && priceUploads.length > 0 ? (
            <PendingContainer>
              <PendingIncomingPanelComponent priceUploads={priceUploads} />
            </PendingContainer>
          ) : null}
        </>
      </Content>
      <ModalFooter>
        <TextButton type={TextButtonType.Tertiary} onClick={onClose}>
          {Locale.translate(Translation.TC_BUTTON_CANCEL)}
        </TextButton>
        <TextButton type={TextButtonType.Primary} disabled={!canUpload} onClick={handleUpload}>
          {Locale.translate(Translation.TC_BUTTON_UPLOAD)}
        </TextButton>
      </ModalFooter>
    </ModalWindow>
  )
}

export const IncomingUploadDialog = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(IncomingUploadDialogComponent)
)
